import React from "react";
import {
	Autocomplete,
	AutocompleteProps,
	AutocompleteRenderInputParams,
	CircularProgress,
	FormControl,
	FormHelperText,
	InputLabel,
	Select,
	TextField,
} from "@mui/material";
import { ConditionalNullable, InputError } from "./types";
import { decodeNativeSelectKey, encodeNativeSelectKey } from "./utils";
import i18n from "i18next";
import { EMPTY_SELECTION, LOADING_OPTION_VALUE } from "./constants";
import { isTouchDevice } from "src/utils/isTouchDevice";
import { CustomPopper } from "src/components/common/popper/CustomPopper.tsx";

export interface SelectFieldProps<T, Key, DisableClearable extends boolean | undefined>
	extends Omit<
		AutocompleteProps<T, false, DisableClearable, false>,
		| "onChange"
		| "renderInput"
		| "freeSolo"
		| "multiple"
		| "isOptionEqualToValue"
		| "getOptionKey"
		| "getOptionLabel"
		| "value"
		| "defaultValue"
		| "onFocus"
	> {
	onChange?: (v: ConditionalNullable<Key, DisableClearable>) => void;
	value?: Key | null;
	defaultValue?: Key | null;
	getOptionKey: (o: T) => Key;
	getOptionLabel: (o: T) => string;
	label: string;
	error?: InputError;
	onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement | HTMLDivElement>;
	minWidth?: number | string;
	dropdownWidth?: number | string;
}

export const SelectField = <T, Key, DisableClearable extends boolean | undefined>(
	props: SelectFieldProps<T, Key, DisableClearable>,
) => {
	if (isTouchDevice()) return <NativeSelectField {...props} />;
	else return <AutoCompleteSelectField {...props} />;
};

const AutoCompleteSelectField = <T, Key, DisableClearable extends boolean | undefined>({
	label,
	disableClearable,
	value,
	defaultValue,
	onChange,
	options,
	getOptionKey,
	getOptionLabel,
	loading,
	error = undefined,
	onFocus,
	sx,
	minWidth,
	dropdownWidth,
	...other
}: SelectFieldProps<T, Key, DisableClearable>) => {
	const getOptionForKey = (key: Key | null | undefined) => {
		if (key === undefined) return undefined;

		const option =
			options.find((o) => {
				return getOptionKey(o) === key;
			}) ?? null;

		return option as ConditionalNullable<T, DisableClearable>;
	};

	return (
		<Autocomplete
			sx={{
				minWidth: minWidth,
				...sx,
			}}
			onFocus={onFocus}
			options={loading ? [] : options}
			getOptionLabel={getOptionLabel}
			disableClearable={disableClearable}
			isOptionEqualToValue={(o1, o2) => {
				return getOptionKey(o1) === getOptionKey(o2);
			}}
			onChange={(_, value) => {
				if (value === null) onChange?.(null as ConditionalNullable<Key, DisableClearable>);
				else onChange?.(getOptionKey(value) as ConditionalNullable<Key, DisableClearable>);
			}}
			value={getOptionForKey(value)}
			defaultValue={getOptionForKey(defaultValue)}
			loading={loading}
			PopperComponent={(popperProps) => (
				<CustomPopper {...popperProps} width={dropdownWidth} />
			)}
			renderInput={(params) => {
				return (
					<TextField
						{...params}
						label={label}
						error={error !== undefined}
						helperText={error ?? undefined}
						inputProps={{
							...params.inputProps,
							style: {
								...getAutocompleteInputStyle(label, params),
								...params.inputProps.style,
							},
						}}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<React.Fragment>
									{loading ? (
										<CircularProgress color="inherit" size={20} />
									) : null}
									{params.InputProps.endAdornment}
								</React.Fragment>
							),
						}}
					/>
				);
			}}
			{...other}
		/>
	);
};

const getAutocompleteInputStyle = (
	label: string,
	renderInputParams: AutocompleteRenderInputParams,
) => {
	let textLength;
	const inputValue = renderInputParams.inputProps.value;
	if (typeof inputValue === "string" && inputValue !== "") {
		textLength = inputValue.length;
	} else {
		textLength = label.length;
	}
	return {
		minWidth: 0,
		width: `${textLength + 1}ch`,
	};
};

const NativeSelectField = <T, Key, DisableClearable extends boolean | undefined>({
	label,
	disableClearable,
	value,
	defaultValue,
	onChange,
	options,
	getOptionKey,
	getOptionLabel,
	loading,
	error = undefined,
	onFocus,
	sx
}: SelectFieldProps<T, Key, DisableClearable>) => {
	return (
		<FormControl error={error !== undefined} sx={sx}>
			<InputLabel shrink htmlFor="native-select-for-mobile">
				{label}
			</InputLabel>
			<Select
				native
				label={label}
				value={encodeNativeSelectValue(value, loading)}
				defaultValue={
					defaultValue == undefined ? undefined : encodeNativeSelectKey(defaultValue)
				}
				onFocus={onFocus}
				inputProps={{
					name: label,
					id: "native-select-for-mobile",
				}}
				onChange={(e) => {
					onChange?.(decodeNativeSelectKey(e.target.value));
				}}
			>
				<option
					hidden={!loading}
					disabled={true}
					value={encodeNativeSelectKey(LOADING_OPTION_VALUE)}
				>
					{i18n.t("loading...")}
				</option>
				{options.map((o, index) => {
					return (
						<option
							key={`select-field-option-${index}`}
							value={encodeNativeSelectKey(getOptionKey(o))}
							hidden={loading}
						>
							{getOptionLabel(o)}
						</option>
					);
				})}
				<option
					hidden={disableClearable || loading}
					value={encodeNativeSelectKey(EMPTY_SELECTION)}
					disabled={disableClearable}
				>
					{i18n.t("undefined")}
				</option>
			</Select>
			<FormHelperText>{error ?? ""}</FormHelperText>
		</FormControl>
	);
};

const encodeNativeSelectValue = <Key,>(
	value: Key | null | undefined,
	loading: boolean | undefined,
) => {
	if (value === undefined) return undefined;
	if (loading === true) return encodeNativeSelectKey(LOADING_OPTION_VALUE);
	if (value === null || value === "") return encodeNativeSelectKey(EMPTY_SELECTION);
	return encodeNativeSelectKey(value);
};
